// services/business/dashboard.service.js
const API_BASE = 'https://api.jobeng.ai';

class BusinessDashboardService {
  constructor() {
    this.baseUrl = API_BASE;
  }

  // =====================================================
  // DASHBOARD OVERVIEW
  // =====================================================

  /**
   * Get complete dashboard data for a company
   * @param {number} companyId - Company ID (default: 1)
   */
  async getDashboardData(companyId = 1) {
    try {
      // Fetch all dashboard data in parallel
      const [
        companyProfile,
        businessStats,
        teamMembers,
        subscription,
        recentActivities,
        quickStats
      ] = await Promise.all([
        this.getCompanyProfile(companyId),
        this.getBusinessStats(companyId),
        this.getTeamMembers(companyId),
        this.getSubscription(companyId),
        this.getRecentActivities(companyId),
        this.getQuickStats(companyId)
      ]);

      return {
        success: true,
        data: {
          company: companyProfile,
          stats: businessStats,
          team: teamMembers,
          subscription: subscription,
          activities: recentActivities,
          quickStats: quickStats,
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return {
        success: false,
        error: 'Failed to load dashboard data',
        data: this.getMockDashboardData() // Fallback to mock data
      };
    }
  }

  // =====================================================
  // COMPANY PROFILE
  // =====================================================

  /**
   * Get company profile
   * @param {number} companyId - Company ID
   */
  async getCompanyProfile(companyId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/company_profiles/${companyId}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        return this.formatCompanyProfile(result.data);
      }
      return this.getMockCompanyProfile();
    } catch (error) {
      console.error('Error fetching company profile:', error);
      return this.getMockCompanyProfile();
    }
  }

  /**
   * Update company profile
   * @param {number} companyId - Company ID
   * @param {object} profileData - Updated profile data
   */
  async updateCompanyProfile(companyId, profileData) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/company_profiles/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData)
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating company profile:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // BUSINESS STATISTICS
  // =====================================================

  /**
   * Get business statistics from various endpoints
   * @param {number} companyId - Company ID
   */
  async getBusinessStats(companyId) {
    try {
      // Fetch stats from multiple endpoints in parallel
      const [
        jobsRes,
        projectsRes,
        clientsRes,
        vendorsRes,
        invoicesRes,
        campaignsRes
      ] = await Promise.allSettled([
        fetch(`${this.baseUrl}/api/data/jobs?company_id=${companyId}&status=active`),
        fetch(`${this.baseUrl}/api/data/projects?company_id=${companyId}&status=active`),
        fetch(`${this.baseUrl}/api/data/clients?company_id=${companyId}`),
        fetch(`${this.baseUrl}/api/data/vendors?company_id=${companyId}`),
        fetch(`${this.baseUrl}/api/data/invoices?company_id=${companyId}`),
        fetch(`${this.baseUrl}/api/data/campaigns?company_id=${companyId}`)
      ]);

      const stats = {
        activeJobs: await this.extractCount(jobsRes),
        activeProjects: await this.extractCount(projectsRes),
        totalClients: await this.extractCount(clientsRes),
        activeVendors: await this.extractCount(vendorsRes),
        pendingInvoices: await this.extractCount(invoicesRes),
        activeCampaigns: await this.extractCount(campaignsRes),
        revenue: 450000, // This would come from a financial endpoint
        applications: 47,
        interviews: 12,
        offers: 3
      };

      return stats;
    } catch (error) {
      console.error('Error fetching business stats:', error);
      return this.getMockStats();
    }
  }

  /**
   * Extract count from API response
   */
  async extractCount(promiseResult) {
    if (promiseResult.status === 'fulfilled' && promiseResult.value.ok) {
      const data = await promiseResult.value.json();
      return data.data?.length || data.count || 0;
    }
    return 0;
  }

  // =====================================================
  // TEAM MEMBERS
  // =====================================================

  /**
   * Get team members for a company
   * @param {number} companyId - Company ID
   */
  async getTeamMembers(companyId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/team_members?company_id=${companyId}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        return this.formatTeamMembers(result.data);
      }
      return this.getMockTeamMembers();
    } catch (error) {
      console.error('Error fetching team members:', error);
      return this.getMockTeamMembers();
    }
  }

  /**
   * Get team member by ID
   * @param {number} memberId - Team member ID
   */
  async getTeamMember(memberId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/team_members/${memberId}`);
      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('Error fetching team member:', error);
      return null;
    }
  }

  /**
   * Update team member status (online/offline)
   * @param {number} memberId - Team member ID
   * @param {string} status - New status
   */
  async updateTeamMemberStatus(memberId, status) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/team_members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, last_active: new Date().toISOString() })
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating team member status:', error);
      return { success: false };
    }
  }

  // =====================================================
  // SUBSCRIPTION MANAGEMENT
  // =====================================================

  /**
   * Get company subscription
   * @param {number} companyId - Company ID
   */
  async getSubscription(companyId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/business-subscriptions?company_id=${companyId}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const subData = Array.isArray(result.data) ? result.data[0] : result.data;
        return this.formatSubscription(subData);
      }
      return this.getMockSubscription();
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return this.getMockSubscription();
    }
  }

  /**
   * Check if a feature is available for the current subscription
   * @param {object} subscription - Subscription object
   * @param {string} feature - Feature to check
   */
  isFeatureAvailable(subscription, feature) {
    if (!subscription) return false;
    
    const planFeatures = {
      'starter': ['post_job', 'view_applicants', 'schedule_interview', 'manage_offers', 'company_profile'],
      'professional': [...this.starterFeatures, 'project_management', 'vendor_management', 'financial_reports'],
      'team': [...this.professionalFeatures, 'team_management', 'employee_management', 'payroll_management'],
      'enterprise': [...this.teamFeatures, 'marketing_campaigns', 'advanced_analytics', 'ai_insights']
    };

    const planType = subscription.plan_type || 'starter';
    return planFeatures[planType]?.includes(feature) || false;
  }

  // =====================================================
  // NOTIFICATIONS
  // =====================================================

  /**
   * Get recent notifications
   * @param {number} companyId - Company ID
   * @param {number} limit - Number of notifications to fetch
   */
  async getNotifications(companyId, limit = 10) {
    try {
      // Try multiple endpoints that might contain notifications
      const endpoints = [
        `${this.baseUrl}/api/erp/notifications?company_id=${companyId}&limit=${limit}`,
        `${this.baseUrl}/business-notifications?company_id=${companyId}&limit=${limit}`,
        `${this.baseUrl}/api/data/notifications?company_id=${companyId}&limit=${limit}`
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint);
          if (response.ok) {
            const result = await response.json();
            const notifications = result.data || result.notifications || [];
            return this.formatNotifications(notifications);
          }
        } catch (e) {
          continue;
        }
      }
      
      return this.getMockNotifications();
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return this.getMockNotifications();
    }
  }

  /**
   * Mark notification as read
   * @param {number} notificationId - Notification ID
   */
  async markNotificationRead(notificationId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/data/notifications/${notificationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_read: true })
      });
      return await response.json();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false };
    }
  }

  // =====================================================
  // ACTIVITIES
  // =====================================================

  /**
   * Get recent activities
   * @param {number} companyId - Company ID
   * @param {number} limit - Number of activities to fetch
   */
  async getRecentActivities(companyId, limit = 10) {
    try {
      // Try to get from activity log endpoint
      const response = await fetch(`${this.baseUrl}/api/data/activity_log?company_id=${companyId}&limit=${limit}&sort=created_at&order=DESC`);
      
      if (response.ok) {
        const result = await response.json();
        return this.formatActivities(result.data || []);
      }
      
      return this.getMockActivities();
    } catch (error) {
      console.error('Error fetching activities:', error);
      return this.getMockActivities();
    }
  }

  // =====================================================
  // QUICK STATS
  // =====================================================

  /**
   * Get quick statistics for dashboard
   * @param {number} companyId - Company ID
   */
  async getQuickStats(companyId) {
    try {
      const [stats, notifications] = await Promise.all([
        this.getBusinessStats(companyId),
        this.getNotifications(companyId, 1)
      ]);

      return {
        pendingTasks: stats.activeJobs || 0,
        overdueInvoices: 0, // This would come from invoices endpoint
        lowStockItems: 23, // This would come from inventory
        pendingApprovals: stats.offers || 0,
        newLeads: stats.applications || 0,
        upcomingPayroll: '8 days',
        unreadNotifications: notifications.filter(n => !n.read).length
      };
    } catch (error) {
      console.error('Error getting quick stats:', error);
      return this.getMockQuickStats();
    }
  }

  // =====================================================
  // FORMATTING FUNCTIONS
  // =====================================================

  formatCompanyProfile(data) {
    return {
      id: data.id,
      name: data.company_name || data.name,
      legal_name: data.legal_name,
      email: data.email,
      phone: data.phone,
      industry: data.industry,
      employee_count: data.employee_count || data.employees || 0,
      founded_date: data.founded_date,
      logo: data.logo,
      website: data.website,
      address: data.address,
      city: data.city,
      country: data.country,
      vat_number: data.vat_number,
      registration_number: data.registration_number,
      company_size: data.company_size,
      company_type: data.company_type,
      description: data.description,
      linkedin: data.linkedin,
      twitter: data.twitter,
      facebook: data.facebook,
      subscription_plan: data.subscription_plan,
      subscription_status: data.subscription_status,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  formatTeamMembers(data) {
    return (data || []).map(member => ({
      id: member.id,
      name: member.name || member.full_name,
      email: member.email,
      role: member.role,
      department: member.department,
      status: member.status || 'offline',
      last_active: member.last_active,
      avatar: member.avatar,
      phone: member.phone,
      job_title: member.job_title
    }));
  }

  formatSubscription(data) {
    return {
      id: data.id,
      plan_id: data.plan_id,
      plan_type: data.plan_type,
      plan_name: data.plan_name || data.plan_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      status: data.status || 'active',
      start_date: data.start_date,
      end_date: data.end_date,
      billing_cycle: data.billing_cycle || 'monthly',
      price: data.price || data.amount,
      currency: data.currency || 'GBP',
      users: data.users || data.users_count || 1,
      features: data.features || []
    };
  }

  formatNotifications(data) {
    return (data || []).map(notif => ({
      id: notif.id,
      title: notif.title,
      message: notif.message || notif.description,
      type: notif.type || 'info',
      priority: notif.priority || 'medium',
      read: notif.is_read || notif.read || false,
      time: notif.created_at,
      icon: this.getNotificationIcon(notif.type),
      color: this.getNotificationColor(notif.priority)
    }));
  }

  formatActivities(data) {
    return (data || []).map(activity => ({
      id: activity.id,
      type: activity.activity_type || activity.type,
      title: activity.title,
      description: activity.description,
      user: activity.user_name || activity.user,
      time: activity.created_at,
      icon: this.getActivityIcon(activity.type),
      color: this.getActivityColor(activity.type)
    }));
  }

  getNotificationIcon(type) {
    const icons = {
      'application': 'account-plus',
      'interview': 'calendar-clock',
      'offer': 'email-check',
      'project': 'clipboard-list',
      'invoice': 'file-document',
      'payment': 'credit-card',
      'alert': 'alert-circle'
    };
    return icons[type] || 'information';
  }

  getNotificationColor(priority) {
    const colors = {
      'high': '#ef4444',
      'medium': '#f59e0b',
      'low': '#10b981'
    };
    return colors[priority] || '#64748B';
  }

  getActivityIcon(type) {
    const icons = {
      'job': 'briefcase-plus',
      'application': 'account-plus',
      'interview': 'calendar-clock',
      'offer': 'handshake',
      'project': 'clipboard-list',
      'invoice': 'file-document'
    };
    return icons[type] || 'information';
  }

  getActivityColor(type) {
    const colors = {
      'job': '#3b82f6',
      'application': '#10b981',
      'interview': '#f59e0b',
      'offer': '#8b5cf6',
      'project': '#6366f1',
      'invoice': '#0e4e96'
    };
    return colors[type] || '#64748B';
  }

  // =====================================================
  // MOCK DATA (Fallback when API fails)
  // =====================================================

  getMockDashboardData() {
    return {
      company: this.getMockCompanyProfile(),
      stats: this.getMockStats(),
      team: this.getMockTeamMembers(),
      subscription: this.getMockSubscription(),
      activities: this.getMockActivities(),
      quickStats: this.getMockQuickStats(),
      notifications: this.getMockNotifications()
    };
  }

  getMockCompanyProfile() {
    return {
      id: 1,
      name: 'Jobeng Limited',
      industry: 'Construction & Engineering',
      employee_count: 45,
      logo: null,
      founded_date: '2015-01-01',
      company_size: '11-50',
      subscription_plan: 'professional',
      subscription_status: 'active'
    };
  }

  getMockStats() {
    return {
      activeJobs: 5,
      activeProjects: 8,
      totalClients: 24,
      activeVendors: 15,
      pendingInvoices: 3,
      activeCampaigns: 2,
      revenue: 450000,
      applications: 47,
      interviews: 12,
      offers: 3
    };
  }

  getMockTeamMembers() {
    return [
      { id: 1, name: 'Sarah Johnson', email: 'sarah@jobeng.com', role: 'Senior Sales Manager', department: 'Sales', status: 'online', last_active: new Date().toISOString() },
      { id: 2, name: 'Mike Chen', email: 'mike@jobeng.com', role: 'Sales Representative', department: 'Sales', status: 'online', last_active: new Date().toISOString() },
      { id: 3, name: 'David Brown', email: 'david@jobeng.com', role: 'Sales Manager', department: 'Sales', status: 'away', last_active: new Date(Date.now() - 3600000).toISOString() },
      { id: 4, name: 'Emma Wilson', email: 'emma@jobeng.com', role: 'Sales Representative', department: 'Sales', status: 'offline', last_active: new Date(Date.now() - 86400000).toISOString() }
    ];
  }

  getMockSubscription() {
    return {
      id: 1,
      plan_type: 'professional',
      plan_name: 'Professional Plan',
      status: 'active',
      start_date: '2026-01-01',
      end_date: '2026-12-31',
      billing_cycle: 'annual',
      price: 599.99,
      currency: 'GBP',
      users: 10,
      features: ['Project Management', 'Vendor Management', 'Financial Reports']
    };
  }

  getMockActivities() {
    return [
      { id: 1, type: 'job', title: 'New Job Posted', description: 'Senior Developer position created', user: 'Sarah Johnson', time: new Date(Date.now() - 3600000).toISOString() },
      { id: 2, type: 'application', title: 'New Application', description: 'Mike Chen applied for Project Manager', user: 'System', time: new Date(Date.now() - 7200000).toISOString() },
      { id: 3, type: 'interview', title: 'Interview Scheduled', description: 'Interview with Emma Wilson at 2PM', user: 'David Brown', time: new Date(Date.now() - 86400000).toISOString() }
    ];
  }

  getMockNotifications() {
    return [
      { id: 1, title: 'New Application', message: 'John Doe applied for Senior Developer', type: 'application', priority: 'high', read: false, time: new Date(Date.now() - 1800000).toISOString() },
      { id: 2, title: 'Interview Tomorrow', message: 'Interview with Jane Smith at 10AM', type: 'interview', priority: 'medium', read: false, time: new Date(Date.now() - 3600000).toISOString() },
      { id: 3, title: 'Offer Sent', message: 'Offer sent to Mike Johnson', type: 'offer', priority: 'medium', read: true, time: new Date(Date.now() - 86400000).toISOString() }
    ];
  }

  getMockQuickStats() {
    return {
      pendingTasks: 14,
      overdueInvoices: 3,
      lowStockItems: 23,
      pendingApprovals: 7,
      newLeads: 4,
      upcomingPayroll: '8 days',
      unreadNotifications: 2
    };
  }
}

export default new BusinessDashboardService();